import * as PIXI from 'pixi.js-legacy';
import DynamicProppingAncestor from '../DynamicProppingAncestor';
import { translate } from '../../services/L10n';
import { t } from '../../translations';
import productsService from '../../services/products';
import geometry from '../util/geometry';
import Upright from './Upright';
import platform from '../../util/platform';
import tacHelpers from '../../state/tac/tacHelpers';
import { getWidthText } from '../util/dimensionDisplay';
import { ITEMS } from '../../constants';
import productService from '../../services/products';
import {
  selectIsRtl,
  selectUseMetric,
} from '../../state/dexfSettings/dexfSettingsSelectors';
import store from '../../state';

export default class Section extends DynamicProppingAncestor {
  update(args) {
    super.update(args);
    const section = args.item;
    // pad a bit on the front to reach and collide with the sections
    this.leftUprights = this.app.tac.items.filter(
      item =>
        productsService.isType(item, ITEMS.UPRIGHT) &&
        item.x + productService.getPostWidth() / 2 === section.x &&
        geometry.collides(item, section, { front: 20 })
    );

    this.leftUprights.forEach(upright => {
      const sprite = upright && this.app.getSprite(upright.itemid);
      sprite && sprite.cleanOutSectionSprites();
      sprite && sprite.addSection(this);
    });
  }

  cleanUpHintObjects() {
    if (this.app.hintContainer && this.app.hintContainer.children.length) {
      this.app.hintContainer &&
        this.app.hintContainer.removeChild(this.textObject, this.background);
    }
  }

  destroy(args) {
    this.leftUprights.forEach(upright => {
      const sprite = upright && this.app.getSprite(upright.itemid);
      sprite && sprite.removeSection(this);
    });
    this.cleanUpHintObjects();
    super.destroy(args);
  }

  getGlobalPos() {
    const { data, item, origo, ratio, parentItem } = this;

    //Stolen from Item.place()
    return {
      x:
        (item.x -
          (data.projectedVertices[4][0] - data.projectedVertices[0][0]) -
          origo.x) *
        ratio,
      y:
        (parentItem.height - (data.faceSize.height + item.y) + origo.y) * ratio,
    };
  }

  writeWidth() {
    const style = new PIXI.TextStyle({
      fontSize: platform.isKiosk ? 20 : 12,
      fontFamily: 'NotoIKEALatin, Verdana, sans-serif',
      lineHeight: platform.isKiosk ? 1.2 : 1.33,
      trim: this.isMobile,
      fill: '#222',
    });

    const textPadding = 4;

    const measure = getWidthText(this);
    const useMetricMeasures = selectUseMetric(store.getState());
    const unitText = useMetricMeasures
      ? translate(t.MEASURE_VALUE_CM)
      : translate(t.MEASURE_VALUE_IN);
    const isRtl = selectIsRtl(store.getState());
    const textContent = isRtl
      ? `${unitText}${String.fromCharCode(parseInt('200E', 16))} ${measure}`
      : `${measure} ${unitText}`;
    if (!this.textObject) {
      this.textObject = new PIXI.Text(textContent, style);
    } else {
      this.textObject.text = textContent;
    }

    this.cleanUpHintObjects();

    const { faceSize, scaledVertices } = this.data;

    const globalPos = this.getGlobalPos();
    this.textObject.x =
      globalPos.x + scaledVertices[4][0] + (faceSize.width / 2) * this.ratio;
    this.textObject.anchor.set(0.5, 0);
    this.textObject.y = globalPos.y + this.sprite.height * 0.29;

    this.background = new PIXI.Graphics();

    const { textObject } = this;

    this.background.lineStyle(0.75, 0x000000);
    this.background.beginFill(0xffffff);
    this.background.drawRoundedRect(
      textObject.x - textPadding - textObject.width / 2,
      textObject.y - textPadding,
      textObject.width + textPadding * 2,
      textObject.height + textPadding * 2,
      2
    );
    this.background.endFill();

    this.app.hintContainer.addChild(this.background, this.textObject);
  }

  removeSectionHighlight() {
    this.sprite.alpha = 0;
    this.cleanUpHintObjects();
  }

  shouldDisplaySectionHint() {
    const { app, item } = this;

    const draggingItem = app.dragging && app.dragging.item;
    const connectedUprights = item.uprights;
    if (item.width > 800) {
      // no hints for LAGKAPTEN sections
      return false;
    }

    if (item.isTempItem) {
      return true;
    }
    if (!draggingItem) {
      return false;
    }
    if (
      tacHelpers.isWithinWall(draggingItem, app.tac) &&
      connectedUprights &&
      connectedUprights.some(upright => upright.itemid === draggingItem.itemid)
    ) {
      return true;
    }
  }

  draw() {
    const { item, ratio } = this;
    const { faceSize } = this.data;
    const { scaledVertices } = this.data;
    const padding = 2;

    this.sprite = this.sprite || new PIXI.Sprite();

    this.sprite.visible = !item.hidden;
    this.sprite.width = faceSize.width * ratio - padding * 2;
    this.sprite.height = faceSize.height * ratio;
    this.sprite.x = scaledVertices[4][0] + padding;

    if (!this.sprite.parent) {
      this.addChild(this.sprite);
    }

    this.dropAreas = this.dropAreas || new PIXI.Container();
    this.dropAreas.x = scaledVertices[4][0] - scaledVertices[0][0];

    if (!this.dropAreas.parent) {
      this.addChild(this.dropAreas);
    }

    this.childrenContainer = this.childrenContainer || new PIXI.Container();
    this.childrenContainer.x = scaledVertices[4][0] - scaledVertices[0][0];

    if (!this.childrenContainer.parent) {
      this.addChild(this.childrenContainer);
    }

    if (this.shouldDisplaySectionHint()) {
      this.sprite.texture = PIXI.Texture.WHITE;
      this.sprite.tint = 0x0058a3;
      this.sprite.alpha = 0.2;
      this.writeWidth();
    } else {
      this.removeSectionHighlight();
    }

    if (item.keepDuringDrag) {
      this.alpha = 0.5;
      this.removeSectionHighlight();
    } else {
      this.alpha = 1;
    }

    this.drawChildren();
  }

  drawDropAreas(slots) {
    slots = slots.filter(
      slot =>
        this.leftUprights.find(
          upright => upright.itemid === slot.parent.itemid
        ) && this.item.width === slot.width
    );

    const width = this.data.faceSize.width * this.ratio;

    slots.forEach(slot => {
      const localSlot = { ...slot, ...slot.local, y: slot.y - this.item.y };
      const sprite = this.getDropAreaSprite(localSlot, width);
      sprite && this.dropAreas.addChild(sprite);
    });
  }

  displayDropArea(dropareas, slot) {
    const localSlot = slot && {
      ...slot,
      ...slot.local,
      y: slot.y - this.item.y,
    };
    dropareas.children.forEach(dropArea => {
      this.toggleDropAreaVisibility(dropArea, localSlot);
    });
  }
  // override Item.getSelfHitArea
  getSelfHitArea() {
    const { scaledVertices } = this.data;

    let topCrop = 0;
    if (!this.item.filter.switchable) {
      const kids = this.getAllChildren();
      if (!kids.length) {
        return {
          regions: [[]],
        };
      }
      topCrop = kids[0].y;
    }

    // calc only using back side, and tighten it a bit
    const vertices = [
      [scaledVertices[4][0] + Upright.hitAreaPadding, scaledVertices[4][1]],
      [scaledVertices[5][0] - Upright.hitAreaPadding, scaledVertices[5][1]],
      [
        scaledVertices[6][0] - Upright.hitAreaPadding,
        scaledVertices[6][1] + topCrop,
      ],
      [
        scaledVertices[7][0] + Upright.hitAreaPadding,
        scaledVertices[7][1] + topCrop,
      ],
    ];

    return {
      regions: [vertices],
    };
  }
}
